/**
 * Cliente API + loader do Jitsi Meet External API.
 *
 * O servidor Jitsi serve external_api.js em https://{domain}/external_api.js
 * Carregamos sob demanda — sem dependência npm extra, sem inflar bundle.
 */

import axios from '../config/axios';
import { getApiUrl } from '../config/environment';

export interface JoinPayload {
  token: string;
  domain: string;
  roomName: string;
  expiresAt: string;
  displayName: string;
  appointmentId: string;
  appointment: {
    date: string;
    startTime: string;
    durationMinutes: number;
  };
  /** Preenchido no join do profissional — evolução clínica ao lado da sala. */
  patient?: { id: string; full_name: string };
  sessionId?: string;
}

export interface TeleconsultStatus {
  appointmentId: string;
  enabled: boolean;
  roomName: string | null;
  patientUrl: string | null;
  patientTokenExpiresAt: string | null;
  startedAt: string | null;
  endedAt: string | null;
}

export interface EnableResult {
  appointmentId: string;
  roomName: string;
  patientUrl: string;
  patientTokenExpiresAt: string | null;
}

const base = () => `${getApiUrl()}/clinic/teleconsult`;
const publicBase = () => `${getApiUrl()}/public/teleconsult`;

// =====================================================================
// API — Profissional / Equipe (autenticado)
// =====================================================================

export async function getTeleconsultStatus(appointmentId: string): Promise<TeleconsultStatus> {
  const res = await axios.get(`${base()}/appointments/${appointmentId}`);
  return res.data?.data;
}

export async function enableTeleconsult(appointmentId: string): Promise<EnableResult> {
  const res = await axios.post(`${base()}/appointments/${appointmentId}/enable`);
  return res.data?.data;
}

export async function disableTeleconsult(appointmentId: string): Promise<void> {
  await axios.post(`${base()}/appointments/${appointmentId}/disable`);
}

export async function joinAsProfessional(appointmentId: string): Promise<JoinPayload> {
  const res = await axios.post(`${base()}/appointments/${appointmentId}/join`);
  return res.data?.data;
}

export async function leaveSessionAuth(sessionId: string): Promise<void> {
  try {
    await axios.post(`${base()}/sessions/${sessionId}/leave`);
  } catch {
    // best-effort
  }
}

// =====================================================================
// Gravação no browser (áudio da aba → R2 → n8n)
// =====================================================================

export interface BrowserRecordingPresign {
  appointmentId: string;
  objectKey: string;
  uploadUrl: string;
  contentType: string;
  expiresInSeconds: number;
}

export async function presignBrowserRecording(
  appointmentId: string,
  fileName: string,
  contentType: string
): Promise<BrowserRecordingPresign> {
  const res = await axios.post(`${base()}/appointments/${appointmentId}/recording/presign`, {
    fileName,
    contentType,
  });
  const data = res.data?.data;
  if (!res.data?.success || !data?.uploadUrl || !data?.objectKey) {
    throw new Error(
      res.data?.error || 'Servidor não preparou o upload da gravação (verifique R2 no backend).',
    );
  }
  return data;
}

export async function completeBrowserRecording(
  appointmentId: string,
  objectKey: string
): Promise<{ appointmentId: string; recordingUrl: string | null }> {
  const res = await axios.post(`${base()}/appointments/${appointmentId}/recording/complete`, {
    objectKey,
  });
  if (!res.data?.success) {
    throw new Error(
      res.data?.error || 'Falha ao finalizar gravação no servidor (n8n pode não ter sido acionado).',
    );
  }
  return res.data?.data;
}

export async function uploadBlobToPresignedUrl(
  uploadUrl: string,
  blob: Blob,
  contentType: string
): Promise<void> {
  const res = await fetch(uploadUrl, {
    method: 'PUT',
    body: blob,
    headers: { 'Content-Type': contentType },
  });
  if (!res.ok) {
    throw new Error(`Falha no upload da gravação (${res.status}).`);
  }
}

// =====================================================================
// API — Paciente (público, token opaco)
// =====================================================================

export interface PatientPreview {
  appointmentId: string;
  roomName: string;
  appointment: { date: string; startTime: string; durationMinutes: number };
  expiresAt: string;
}

export async function previewPatientTeleconsult(token: string): Promise<PatientPreview> {
  const res = await axios.get(`${publicBase()}/${encodeURIComponent(token)}`);
  return res.data?.data;
}

export async function joinAsPatient(
  token: string,
  displayName?: string
): Promise<JoinPayload> {
  const res = await axios.post(`${publicBase()}/${encodeURIComponent(token)}/join`, {
    displayName,
  });
  return res.data?.data;
}

export async function leaveSessionPublic(sessionId: string): Promise<void> {
  try {
    await axios.post(`${publicBase()}/sessions/${sessionId}/leave`);
  } catch {
    // best-effort
  }
}

// =====================================================================
// Loader do Jitsi Meet External API
// =====================================================================

declare global {
  interface Window {
    JitsiMeetExternalAPI?: any;
  }
}

const SCRIPT_ID = 'jitsi-external-api';

export async function loadJitsiExternalApi(domain: string): Promise<any> {
  if (typeof window === 'undefined') {
    throw new Error('JitsiMeetExternalAPI só pode ser carregado no browser.');
  }
  if (window.JitsiMeetExternalAPI) return window.JitsiMeetExternalAPI;

  const existing = document.getElementById(SCRIPT_ID) as HTMLScriptElement | null;
  if (existing) {
    return await new Promise((resolve, reject) => {
      existing.addEventListener('load', () => resolve(window.JitsiMeetExternalAPI));
      existing.addEventListener('error', () => reject(new Error('Falha ao carregar Jitsi.')));
    });
  }

  return await new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.async = true;
    script.src = `https://${domain}/external_api.js`;
    script.onload = () => {
      if (window.JitsiMeetExternalAPI) resolve(window.JitsiMeetExternalAPI);
      else reject(new Error('JitsiMeetExternalAPI indisponível após load.'));
    };
    script.onerror = () => reject(new Error('Falha ao baixar external_api.js'));
    document.head.appendChild(script);
  });
}
